import { useState, useEffect } from "react";
import { InputLabel } from "../../../../components/Input/InputLabel";
import LoadingElementIsolated from "../../../../components/loading-element/loading-element-isolated";
import {
  Color,
  Font,
  TransferMethod,
} from "../../../../../core/models/entities/LogoOptions";

interface Props {
  isValid: boolean;
  color?: Color;
  heightMM?: number;
  font?: Font;
  method?: TransferMethod;
  getPreview: (color: Color, heightMM: number, font: Font) => Promise<string>;
  fieldsPosition?: "above" | "below";
}

export function NumberLogoPreview({
  color,
  heightMM,
  font,
  isValid,
  getPreview,
  fieldsPosition = "above",
}: Props) {
  const [preview, setPreview] = useState<string | undefined>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const shouldShowFillMessage = !isValid && !isLoading;

  useEffect(() => {
    if (!isValid) {
      setPreview(undefined);
    }

    setIsLoading(isValid);
    const debounceTimeout = setTimeout(() => {
      if (isValid) {
        getPreview(color!, heightMM!, font!)
          .then((result) => setPreview(result))
          .catch(() => setPreview(undefined))
          .finally(() => setIsLoading(false));
      } else {
        setPreview(undefined);
      }
    }, 1000);

    return () => clearTimeout(debounceTimeout);
  }, [color, heightMM, font, isValid]);

  return (
    <div className={"space-y-0.5 mb-8"}>
      <InputLabel>Preview</InputLabel>
      {
        <div className={"flex rounded border h-32 justify-center items-center"}>
          {isLoading ? (
            <>
              <LoadingElementIsolated
                transparent
                className="h-32 animate-pulse"
              />
            </>
          ) : (
            <>
              {preview ? (
                <img
                  className="max-h-[100%]"
                  src={`data:image/jpeg;base64,${preview}`}
                />
              ) : shouldShowFillMessage ? (
                <p className="px-4 text-center text-xs text-gray-500">
                  Fill out the fields {fieldsPosition} to generate a preview.
                </p>
              ) : null}
            </>
          )}
        </div>
      }
    </div>
  );
}
